// Created on 2026-02-07 by Farhan Khan - Mounting Structure Tab for Quotation
// Uses EXACT same fields and structure as MountingStructureForm.jsx
// Do NOT invent fields - all fields must match MountingStructureForm.jsx exactly
import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Col,
  Form,
  Row,
  Table,
  Card,
  Accordion,
} from "react-bootstrap";
import Select from "react-select";
import solisPowerApi from "../../api/solisPowerApi";
import "./QuotationMountingStructures.css";
import PubSub from "pubsub-js";
import { PREDEFINED_BOM } from "../../constants/bomPredefinedData";
import debounce from "lodash/debounce";

// --- PERFORMANCE OPTIMIZED SUB-COMPONENTS ---
// Created on 2026-03-24 by Farhan Khan - Optimized for large tables
const EditableCell = React.memo(({ value, onChange, placeholder = "", className = "" }) => {
  const [localValue, setLocalValue] = useState(value || "");

  // Sync with prop changes (e.g., undo/redo or reset)
  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  const debouncedOnChange = useRef(
    debounce((nextVal) => onChange(nextVal), 300)
  ).current;

  const handleLocalChange = (e) => {
    const nextVal = e.target.value;
    setLocalValue(nextVal);
    debouncedOnChange(nextVal);
  };

  return (
    <Form.Control
      size="sm"
      value={localValue}
      placeholder={placeholder}
      className={className}
      onChange={handleLocalChange}
    />
  );
});

const ComponentRow = React.memo(({ comp, sIdx, cIdx, shapeOptions, makeOptions, handleComponentFieldChange, setShowAddShapePrompt, deleteComponentRow }) => {
  return (
    <tr>
      <td>
        <Form.Control
          size="sm"
          value={comp.name || ""}
          className="fw-bold px-2 border-0 bg-light"
          onChange={(e) => handleComponentFieldChange(sIdx, cIdx, "name", e.target.value)}
        />
      </td>
      <td>
        <EditableCell
          value={comp.specs || ""}
          className="border-0 bg-light"
          onChange={(val) => handleComponentFieldChange(sIdx, cIdx, "specs", val)}
        />
      </td>
      <td>
        <EditableCell
          value={comp.size || ""}
          className="border-0 bg-light"
          onChange={(val) => handleComponentFieldChange(sIdx, cIdx, "size", val)}
        />
      </td>
      <td>
        <div className="d-flex align-items-center gap-1">
          <div className="flex-fill">
            <Select
              options={shapeOptions}
              value={shapeOptions.find(o => o.value === comp.shape) || null}
              onChange={(opt) => handleComponentFieldChange(sIdx, cIdx, "shape", opt ? opt.value : "")}
              menuPortalTarget={document.body}
              styles={{
                control: b => ({ ...b, minHeight: "32px", fontSize: "0.75rem", border: "0", background: "#f8f9fa" }),
                option: (base) => ({
                  ...base,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  padding: "8px 12px"
                }),
                singleValue: (base) => ({
                  ...base,
                  whiteSpace: "normal"
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 9999,
                  minWidth: "150px"
                }),
                menuPortal: base => ({ ...base, zIndex: 9999 })
              }}
              isClearable
            />
          </div>
          <Button
            size="sm"
            variant="light"
            className="border-0 py-0 px-2 shadow-sm"
            title="Add New Shape"
            onClick={() => setShowAddShapePrompt(true)}
          >
            <i className="fa fa-plus text-primary small"></i>
          </Button>
        </div>
      </td>
      <td>
        <div className="d-flex align-items-center gap-2">
          <div className="flex-fill">
            <Select
              isMulti
              options={makeOptions}
              value={makeOptions.filter(o => (comp.make || []).includes(o.value))}
              onChange={(sel) => handleComponentFieldChange(sIdx, cIdx, "make", sel || [])}
              menuPortalTarget={document.body}
              styles={{
                control: (base) => ({ ...base, minHeight: "32px", fontSize: "0.75rem", border: "0", background: "#f8f9fa" }),
                option: (base) => ({
                  ...base,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  padding: "8px 12px"
                }),
                singleValue: (base) => ({
                  ...base,
                  whiteSpace: "normal"
                }),
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: "#e6f0ff",
                  borderRadius: "6px",
                  padding: "2px 6px",
                  display: "flex",
                  alignItems: "center",
                  marginRight: "4px"
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#333",
                  whiteSpace: "normal"
                }),
                multiValueRemove: (base) => ({
                  ...base,
                  cursor: "pointer"
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 9999,
                  minWidth: "200px"
                }),
                menuPortal: base => ({ ...base, zIndex: 9999 }),
              }}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
            />
          </div>
          <Button
            size="sm"
            variant="link"
            className="text-danger p-0 border-0"
            title="Delete Row"
            onClick={() => deleteComponentRow(sIdx, cIdx, comp.id)}
          >
            <i className="fa fa-trash"></i>
          </Button>
        </div>
      </td>
    </tr>
  );
});

const OthersRow = React.memo(({ comp, sIdx, cIdx, handleComponentFieldChange }) => {
  return (
    <tr>
      <td colSpan="5">
        <div className="d-flex align-items-center">
          <div className="bg-light px-3 py-1 border-end small fw-bold text-muted" style={{ minWidth: "120px" }}>OTHERS</div>
          <EditableCell
            placeholder="Enter any additional requirements/notes..."
            value={(comp.specs === "-" || comp.specs == null) ? "" : comp.specs}
            className="border-0 bg-light-subtle flex-fill"
            onChange={(val) => handleComponentFieldChange(sIdx, cIdx, "specs", val)}
          />
        </div>
      </td>
    </tr>
  );
});

const ComboRow = React.memo(({ ci, sIdx, ciIdx, handleComboFieldChange, deleteItemRow }) => {
  return (
    <tr>
      <td className="text-center">{ci.s_no || ciIdx + 1}</td>
      <td>
        <Select
          defaultValue={ci.roof_type ? { value: ci.roof_type, label: ci.roof_type } : null}
          options={[
            { value: "RCC", label: "RCC" },
            { value: "GROUND", label: "GROUND" },
            { value: "TIN SHED", label: "TIN SHED" }
          ]}
          onChange={(opt) => handleComboFieldChange(sIdx, ciIdx, "roof_type", opt ? opt.value : "")}
          menuPortalTarget={document.body}
          styles={{
            control: b => ({ ...b, minHeight: "32px", fontSize: "0.75rem", border: "0", background: "#f8f9fa" }),
            option: (base) => ({
              ...base,
              whiteSpace: "normal",
              wordBreak: "break-word",
              padding: "8px 12px"
            }),
            singleValue: (base) => ({
              ...base,
              whiteSpace: "normal"
            }),
            menu: (base) => ({
              ...base,
              zIndex: 9999,
              minWidth: "150px"
            }),
            menuPortal: base => ({ ...base, zIndex: 9999 })
          }}
        />
      </td>
      <td>
        <div className="d-flex align-items-center gap-2">
          <EditableCell
            type="number"
            value={ci.no_of_panels || ""}
            className="border-0 bg-light flex-fill"
            onChange={(val) => handleComboFieldChange(sIdx, ciIdx, "no_of_panels", val)}
          />
          <Button
            size="sm"
            variant="link"
            className="text-danger p-0 border-0"
            title="Delete Row"
            onClick={() => deleteItemRow(sIdx, ciIdx, ci.id)}
          >
            <i className="fa fa-trash"></i>
          </Button>
        </div>
      </td>
    </tr>
  );
});

const QuotationMountingStructures = ({
  quotationId,
  mountingStructureItems,
  setMountingStructureItems,
  activeTab
}) => {
  const [bomCategoryOptions, setBomCategoryOptions] = useState([]);
  const [makeOptions, setMakeOptions] = useState([]);
  const [shapeOptions, setShapeOptions] = useState([
    { value: "SQUARE PIPE", label: "SQUARE PIPE" },
    { value: "CHANNEL", label: "CHANNEL" },
    { value: "C-CHANNEL", label: "C-CHANNEL" },
    { value: "CUBE", label: "CUBE" }
  ]);
  const [showAddShapePrompt, setShowAddShapePrompt] = useState(false);
  const [newShapeName, setNewShapeName] = useState("");

  // UNDO / REDO STACKS
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const STACK_LIMIT = 20;

  const initializationRef = useRef(false);

  // Helper for deep cloning
  const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

  // Push current state to undo stack BEFORE change
  const pushToUndo = () => {
    setUndoStack(prev => {
      const newStack = [...prev, deepClone(mountingStructureItems)];
      if (newStack.length > STACK_LIMIT) return newStack.slice(1);
      return newStack;
    });
    setRedoStack([]); // Clear redo on new action
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;
    const previousState = undoStack[undoStack.length - 1];
    setRedoStack(prev => [...prev, deepClone(mountingStructureItems)]);
    setMountingStructureItems(previousState);
    setUndoStack(prev => prev.slice(0, prev.length - 1));
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    const nextState = redoStack[redoStack.length - 1];
    const currentState = deepClone(mountingStructureItems);

    setUndoStack(prev => [...prev, currentState]);
    setMountingStructureItems(nextState);
    setRedoStack(prev => prev.slice(0, prev.length - 1));
  };

  useEffect(() => {
    fetchMountingStructureCategories();
  }, []);

  // Auto-load template for new quotations when tab is clicked
  useEffect(() => {
    if (activeTab === "mounting" && !quotationId) {
      if (!mountingStructureItems.sections || mountingStructureItems.sections.length === 0) {
        initializeFromTemplate();
      }
    }
  }, [activeTab, quotationId]);

  const fetchMountingStructureCategories = async () => {
    try {
      const res = await solisPowerApi.fetchActiveBomCategories();
      if (res) {
        setBomCategoryOptions(res.filter(c => c.bom_type === 'MOUNTING_STRUCTURE').map(c => ({ value: c.id, label: c.category_name })));
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const initializeFromTemplate = async () => {
    if (initializationRef.current) return;
    initializationRef.current = true;

    try {
      let makes = makeOptions;
      if (makes.length === 0) {
        const res = await solisPowerApi.fetchActiveBrands();
        if (res) {
          makes = res.map(b => ({ label: b.name, value: b.id }));
          setMakeOptions(makes);
        }
      }

      const template = deepClone(PREDEFINED_BOM.module_mounting_structure);
      if (!template || !template.sections) return;

      const mappedSections = template.sections.map((section, index) => {
        const item = {
          ...section,
          id: `temp_${Date.now()}_${index}`,
          s_no: index + 1,
          status: "Active"
        };

        if (item.components) {
          item.components = item.components.map(comp => {
            if (comp.make && Array.isArray(comp.make)) {
              comp.make = comp.make.map(label => {
                const found = makes.find(m => m.label.toUpperCase() === label.toUpperCase());
                return found ? found.value : label;
              });
            }
            return comp;
          });
        }
        return item;
      });

      setMountingStructureItems(prev => ({
        ...prev,
        section_name: template.section_name || "MODULE MOUNTING STRUCTURE",
        sections: mappedSections
      }));
    } catch (error) {
      console.error("Error initializing mounting structure template:", error);
    }
  };

  const handleReset = async () => {
    pushToUndo();
    initializationRef.current = false;
    await initializeFromTemplate();
    PubSub.publish("RECORD_SAVED_TOAST", { title: "Success", message: "Reset to template successful" });
  };

  const deleteSection = (index) => {
    pushToUndo();
    setMountingStructureItems(prev => {
      const updatedSections = [...(prev.sections || [])].filter((_, i) => i !== index);
      return { ...prev, sections: updatedSections };
    });
  };

  const createEmptyComponent = () => ({
    id: null,
    name: "",
    specs: "",
    size: "",
    shape: "",
    make: []
  });

  const createEmptyItem = () => ({
    id: null,
    s_no: null,
    name: "",
    type: "",
    specs: "",
    length: null,
    roof_type: "RCC",
    no_of_panels: null
  });

  const addManualRow = () => {
    pushToUndo();
    const newSection = {
      id: `man_${Date.now()}`,
      roof_type: "RCC",
      section_name: "NEW SECTION",
      structure_height_front: null,
      structure_height_back: null,
      components: [createEmptyComponent()],
      status: "Active"
    };
    setMountingStructureItems(prev => ({
      ...prev,
      sections: [...(prev.sections || []), newSection]
    }));
  };

  const addComponentRow = (sectionIdx) => {
    pushToUndo();
    setMountingStructureItems(prev => {
      const updatedSections = [...(prev.sections || [])];
      if (updatedSections[sectionIdx]) {
        const updatedComponents = [...(updatedSections[sectionIdx].components || []), createEmptyComponent()];
        updatedSections[sectionIdx] = { ...updatedSections[sectionIdx], components: updatedComponents };
      }
      return { ...prev, sections: updatedSections };
    });
  };

  const addItemRow = (sectionIdx) => {
    pushToUndo();
    setMountingStructureItems(prev => {
      const updatedSections = [...(prev.sections || [])];
      if (updatedSections[sectionIdx]) {
        const updatedItems = [...(updatedSections[sectionIdx].items || []), createEmptyItem()];
        updatedSections[sectionIdx] = { ...updatedSections[sectionIdx], items: updatedItems };
      }
      return { ...prev, sections: updatedSections };
    });
  };

  const deleteComponentRow = (sectionIdx, compIdx, rowId) => {
    if (window.confirm("Are you sure you want to delete this row?")) {
      pushToUndo();
      // If row has an ID, it's already in DB - but based on instructions I should only handle UI state unless specified otherwise.
      // However, instruction says "call API if rowId exists". 
      // I'll stick to UI state primarily as I don't see a specific deleteRowAPI provided in context for this specific sub-module yet, 
      // but I will implement the logic as requested.
      if (rowId && typeof solisPowerApi.deleteMountingStructureComponent === 'function') {
        solisPowerApi.deleteMountingStructureComponent(rowId).catch(err => console.error(err));
      }

      setMountingStructureItems(prev => {
        const updatedSections = [...(prev.sections || [])];
        if (updatedSections[sectionIdx]) {
          const updatedComponents = updatedSections[sectionIdx].components.filter((_, i) => i !== compIdx);
          updatedSections[sectionIdx] = { ...updatedSections[sectionIdx], components: updatedComponents };
        }
        return { ...prev, sections: updatedSections };
      });
    }
  };

  const deleteItemRow = (sectionIdx, itemIdx, rowId) => {
    if (window.confirm("Are you sure you want to delete this row?")) {
      pushToUndo();
      if (rowId && typeof solisPowerApi.deleteMountingStructureItem === 'function') {
        solisPowerApi.deleteMountingStructureItem(rowId).catch(err => console.error(err));
      }

      setMountingStructureItems(prev => {
        const updatedSections = [...(prev.sections || [])];
        if (updatedSections[sectionIdx]) {
          const updatedItems = updatedSections[sectionIdx].items.filter((_, i) => i !== itemIdx);
          updatedSections[sectionIdx] = { ...updatedSections[sectionIdx], items: updatedItems };
        }
        return { ...prev, sections: updatedSections };
      });
    }
  };

  const handleAddNewShape = () => {
    if (!newShapeName.trim()) return;
    const newVal = newShapeName.trim().toUpperCase();
    if (!shapeOptions.find(o => o.value === newVal)) {
      setShapeOptions([...shapeOptions, { value: newVal, label: newVal }]);
    }
    setNewShapeName("");
    setShowAddShapePrompt(false);
  };

  const handleStrictFieldChange = (sectionIdx, key, val) => {
    pushToUndo();
    setMountingStructureItems(prev => {
      const updatedSections = [...(prev.sections || [])];
      if (updatedSections[sectionIdx]) {
        updatedSections[sectionIdx] = { ...updatedSections[sectionIdx], [key]: val };
      }
      return { ...prev, sections: updatedSections };
    });
  };

  const handleComponentFieldChange = (sectionIdx, compIdx, key, val) => {
    pushToUndo();
    setMountingStructureItems(prev => {
      const updatedSections = [...(prev.sections || [])];
      if (!updatedSections[sectionIdx]) return prev;

      const updatedComponents = [...(updatedSections[sectionIdx].components || [])];
      if (!updatedComponents[compIdx]) return prev;

      let processedVal = val;
      if (key === 'make') {
        processedVal = Array.isArray(val) ? val.map(v => v.value || v) : [];
      } else if (key === 'shape') {
        processedVal = val?.value || val;
      }

      updatedComponents[compIdx] = { ...updatedComponents[compIdx], [key]: processedVal };
      updatedSections[sectionIdx] = { ...updatedSections[sectionIdx], components: updatedComponents };

      return { ...prev, sections: updatedSections };
    });
  };

  // Re-implementing updateRow style for Components for maximum safety
  const updateComponentRow = (sectionIdx, compIdx, field, value) => {
    pushToUndo();
    setMountingStructureItems(prev => {
      const updatedSections = [...(prev.sections || [])];
      if (!updatedSections[sectionIdx]) return prev;

      updatedSections[sectionIdx] = {
        ...updatedSections[sectionIdx],
        components: (updatedSections[sectionIdx].components || []).map((comp, i) =>
          i === compIdx ? { ...comp, [field]: value } : comp
        )
      };

      return { ...prev, sections: updatedSections };
    });
  };

  const handleNestedFieldChange = (sectionIdx, parentKey, key, val) => {
    pushToUndo();
    setMountingStructureItems(prev => {
      const updatedSections = [...(prev.sections || [])];
      if (!updatedSections[sectionIdx]) return prev;

      const updatedNested = { ...(updatedSections[sectionIdx][parentKey] || {}), [key]: val };
      updatedSections[sectionIdx] = { ...updatedSections[sectionIdx], [parentKey]: updatedNested };

      return { ...prev, sections: updatedSections };
    });
  };

  const handleComboFieldChange = (sectionIdx, itemIdx, key, val) => {
    pushToUndo();
    setMountingStructureItems(prev => {
      const updatedSections = [...(prev.sections || [])];
      if (!updatedSections[sectionIdx]) return prev;

      updatedSections[sectionIdx] = {
        ...updatedSections[sectionIdx],
        items: (updatedSections[sectionIdx].items || []).map((item, i) =>
          i === itemIdx ? { ...item, [key]: val } : item
        )
      };

      return { ...prev, sections: updatedSections };
    });
  };

  // ValueContainer logic removed in favor of chip-based tag UI

  const sections = mountingStructureItems.sections || [];

  return (
    <div className="mt-4">
      {/* Header with Undo/Redo/Reset */}
      <Row className="mb-3 align-items-center">
        <Col lg={6}>
          <h5 className="fw-bold mb-0" style={{ color: "#364794" }}>Module Mounting Structure</h5>
          <small className="text-muted">
            {quotationId ? "Editing existing structure" : "New structure from template"}
          </small>
        </Col>
        <Col lg={6} className="text-end d-flex gap-2 justify-content-end align-items-center">
          <div className="btn-group me-2 shadow-sm">
            <Button
              variant="outline-primary"
              size="sm"
              disabled={undoStack.length === 0}
              onClick={handleUndo}
              title="Undo (Ctrl+Z)"
            >
              <i className="fa fa-undo"></i>
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              disabled={redoStack.length === 0}
              onClick={handleRedo}
              title="Redo (Ctrl+Y)"
            >
              <i className="fa fa-repeat"></i>
            </Button>
          </div>
          <Button
            size="sm"
            variant="outline-secondary"
            className="shadow-sm"
            onClick={handleReset}
          >
            <i className="fa fa-refresh me-1"></i> Reset
          </Button>
          <Button size="sm" className="btn-create shadow-sm" onClick={addManualRow}>
            <i className="fa fa-plus me-1"></i> Add Roof Type
          </Button>
        </Col>
      </Row>

      {sections.length === 0 ? (
        <Card className="text-center py-5 shadow-sm border-0 bg-light">
          <Card.Body>
            <i className="fa fa-building fa-3x text-muted mb-3"></i>
            <h6 className="text-muted">No sections available</h6>
            <Button size="sm" className="btn-create mt-2" onClick={handleReset}>
              Load Template
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Accordion alwaysOpen defaultActiveKey={sections.map((_, i) => i.toString())} className="bom-accordion shadow-sm rounded overflow-hidden border-0">
          {sections.map((section, sIdx) => {
            const isRCCorGround = section.components && (section.roof_type === "RCC" || section.roof_type === "GROUND");
            const isTinShed = section.roof_type === "TIN SHED";
            const isCombo = section.roof_type === "COMBO";
            const isSpecial = section.section_name === "SPECIAL STRUCTURE" || section.roof_type === "SPECIAL STRUCTURE";

            return (
              <Accordion.Item key={section.id || sIdx} eventKey={sIdx.toString()} className="border-0 bg-white mb-2 overflow-hidden shadow-sm">
                <Accordion.Header>
                  <div className="d-flex align-items-center justify-content-between w-100 pe-3">
                    <div
                      className="flex-fill me-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Form.Control
                        size="sm"
                        value={section.section_name || section.roof_type || ""}
                        className="fw-bold border-0 bg-transparent p-0"
                        style={{ color: "#364794", fontSize: "1rem" }}
                        onChange={(e) => handleStrictFieldChange(sIdx, "section_name", e.target.value)}
                      />
                    </div>
                    <div className="d-flex align-items-center gap-3">
                      <i
                        className="fa fa-trash text-danger opacity-75 hover-opacity-100 cursor-pointer"
                        title="Delete Section"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm("Are you sure you want to delete this section?")) {
                            deleteSection(sIdx);
                          }
                        }}
                      ></i>
                      <i className="fa fa-chevron-down small text-muted opacity-50"></i>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body className="p-4">
                  {/* RCC / GROUND LAYOUT */}
                  {isRCCorGround && (
                    <>
                      <Row className="mb-4 g-3 align-items-end">
                        <Col lg={3}>
                          <Form.Group>
                            <Form.Label className="form-view-label mb-1">FRONT HEIGHT (FT)</Form.Label>
                            <Form.Control
                              size="sm"
                              type="number"
                              placeholder="0.00"
                              value={section.structure_height_front ?? ""}
                              onChange={(e) => handleStrictFieldChange(sIdx, "structure_height_front", e.target.value)}
                              className="border-secondary-subtle shadow-sm"
                            />
                          </Form.Group>
                        </Col>
                        <Col lg={3}>
                          <Form.Group>
                            <Form.Label className="form-view-label mb-1">BACK HEIGHT (FT)</Form.Label>
                            <Form.Control
                              size="sm"
                              type="number"
                              placeholder="0.00"
                              value={section.structure_height_back ?? ""}
                              onChange={(e) => handleStrictFieldChange(sIdx, "structure_height_back", e.target.value)}
                              className="border-secondary-subtle shadow-sm"
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <div className="table-responsive">
                        <Table bordered hover size="sm" className="mb-0 shadow-sm border-0">
                          <thead className="bg-light align-middle text-uppercase small" style={{ borderBottom: "2px solid #364794" }}>
                            <tr>
                              <th style={{ width: "20%" }}>Name</th>
                              <th style={{ width: "15%" }}>Specs</th>
                              <th style={{ width: "15%" }}>Size</th>
                              <th style={{ width: "20%" }}>Shape</th>
                              <th style={{ width: "25%" }}>Make (Master)</th>
                            </tr>
                          </thead>
                          <tbody className="border-0">
                            {(section.components || []).map((comp, cIdx) => {
                              const isOthers = comp.name === "OTHERS";

                              if (isOthers) {
                                return (
                                  <OthersRow
                                    key={cIdx}
                                    comp={comp}
                                    sIdx={sIdx}
                                    cIdx={cIdx}
                                    handleComponentFieldChange={handleComponentFieldChange}
                                  />
                                );
                              }

                              return (
                                <ComponentRow
                                  key={cIdx}
                                  comp={comp}
                                  sIdx={sIdx}
                                  cIdx={cIdx}
                                  shapeOptions={shapeOptions}
                                  makeOptions={makeOptions}
                                  handleComponentFieldChange={handleComponentFieldChange}
                                  setShowAddShapePrompt={setShowAddShapePrompt}
                                  deleteComponentRow={deleteComponentRow}
                                  ValueContainer={null}
                                />
                              );
                            })}
                          </tbody>
                        </Table>
                        <div className="mt-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => addComponentRow(sIdx)}
                            className="border-dashed"
                          >
                            <i className="fa fa-plus me-1"></i> Add Row
                          </Button>
                        </div>
                      </div>
                    </>
                  )}

                  {/* TIN SHED LAYOUT */}
                  {isTinShed && (
                    <Row className="g-4">
                      <Col lg={6}>
                        <Form.Label className="form-view-label mb-1 text-uppercase small fw-bold">Structure Type</Form.Label>
                        <Select
                          isMulti
                          isSearchable={true}
                          closeMenuOnSelect={false}
                          options={(section.structure_type || []).map(t => ({ value: t, label: t }))}
                          value={(Array.isArray(section.selected_structure_type)
                            ? section.selected_structure_type
                            : (section.selected_structure_type ? [section.selected_structure_type] : [])
                          ).map(t => ({ value: t, label: t }))}
                          onChange={(sel) => handleStrictFieldChange(sIdx, "selected_structure_type", sel ? sel.map(o => o.value) : [])}
                          menuPortalTarget={document.body}
                          styles={{
                            control: b => ({ ...b, border: "1px solid #dee2e6", background: "#f8f9fa" }),
                            option: (base) => ({
                              ...base,
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              padding: "8px 12px"
                            }),
                            multiValue: (base) => ({
                              ...base,
                              backgroundColor: "#eef4ff",
                              borderRadius: "6px",
                              padding: "2px 6px"
                            }),
                            multiValueLabel: (base) => ({
                              ...base,
                              fontSize: "12px",
                              whiteSpace: "normal"
                            }),
                            menu: (base) => ({
                              ...base,
                              zIndex: 9999,
                              minWidth: "200px"
                            }),
                            menuPortal: base => ({ ...base, zIndex: 9999 })
                          }}
                        />
                      </Col>
                      <Col lg={6}>
                        <Form.Label className="form-view-label mb-1 text-uppercase small fw-bold">Mounting Type</Form.Label>
                        <Select
                          isMulti
                          isSearchable={true}
                          closeMenuOnSelect={false}
                          options={(section.mounting_type || []).map(t => ({ value: t, label: t }))}
                          value={(Array.isArray(section.selected_mounting_type)
                            ? section.selected_mounting_type
                            : (section.selected_mounting_type ? [section.selected_mounting_type] : [])
                          ).map(t => ({ value: t, label: t }))}
                          onChange={(sel) => handleStrictFieldChange(sIdx, "selected_mounting_type", sel ? sel.map(o => o.value) : [])}
                          menuPortalTarget={document.body}
                          styles={{
                            control: b => ({ ...b, border: "1px solid #dee2e6", background: "#f8f9fa" }),
                            option: (base) => ({
                              ...base,
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              padding: "8px 12px"
                            }),
                            multiValue: (base) => ({
                              ...base,
                              backgroundColor: "#eef4ff",
                              borderRadius: "6px",
                              padding: "2px 6px"
                            }),
                            multiValueLabel: (base) => ({
                              ...base,
                              fontSize: "12px",
                              whiteSpace: "normal"
                            }),
                            menu: (base) => ({
                              ...base,
                              zIndex: 9999,
                              minWidth: "200px"
                            }),
                            menuPortal: base => ({ ...base, zIndex: 9999 })
                          }}
                        />
                      </Col>
                    </Row>
                  )}

                  {/* COMBO LAYOUT */}
                  {isCombo && (
                    <>
                      <Table bordered size="sm" className="shadow-sm">
                        <thead className="bg-light small text-uppercase">
                          <tr>
                            <th style={{ width: "80px" }}>S.No</th>
                            <th>Roof Type</th>
                            <th style={{ width: "30%" }}>No of Panels</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(section.items || []).map((ci, ciIdx) => (
                            <ComboRow
                              key={ciIdx}
                              ci={ci}
                              sIdx={sIdx}
                              ciIdx={ciIdx}
                              handleComboFieldChange={handleComboFieldChange}
                              deleteItemRow={deleteItemRow}
                            />
                          ))}
                        </tbody>
                      </Table>
                      <div className="mt-2 mb-3">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => addItemRow(sIdx)}
                          className="border-dashed"
                        >
                          <i className="fa fa-plus me-1"></i> Add Row
                        </Button>
                      </div>
                      <div className="alert alert-info py-2 px-3 small border-0 mt-3 bg-light-subtle d-flex align-items-center shadow-sm">
                        <i className="fa fa-info-circle me-2 text-primary"></i> {section.note}
                      </div>
                    </>
                  )}

                  {/* SPECIAL STRUCTURE LAYOUT */}
                  {isSpecial && (
                    <>
                      <Table bordered size="sm" className="shadow-sm">
                        <thead className="bg-light small text-uppercase">
                          <tr>
                            <th>Name</th>
                            <th style={{ width: "25%" }}>Type</th>
                            <th style={{ width: "25%" }}>Specs</th>
                            <th style={{ width: "20%" }}>Length</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(section.items || []).map((si, siIdx) => (
                            <tr key={siIdx}>
                              <td>
                                <Form.Control
                                  size="sm"
                                  value={si.name || ""}
                                  className="fw-bold px-2 border-0 bg-light"
                                  onChange={(e) => handleComboFieldChange(sIdx, siIdx, "name", e.target.value)}
                                />
                              </td>
                              <td>
                                <Form.Control
                                  size="sm"
                                  value={si.type || ""}
                                  placeholder="Type..."
                                  className="border-0 bg-light"
                                  onChange={(e) => handleComboFieldChange(sIdx, siIdx, "type", e.target.value)}
                                />
                              </td>
                              <td>
                                <Form.Control
                                  size="sm"
                                  value={si.specs || ""}
                                  placeholder="Specs..."
                                  className="border-0 bg-light"
                                  onChange={(e) => handleComboFieldChange(sIdx, siIdx, "specs", e.target.value)}
                                />
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <Form.Control
                                    size="sm"
                                    type="number"
                                    value={si.length || ""}
                                    placeholder="0"
                                    className="border-0 bg-light flex-fill"
                                    onChange={(e) => handleComboFieldChange(sIdx, siIdx, "length", e.target.value)}
                                  />
                                  <Button
                                    size="sm"
                                    variant="link"
                                    className="text-danger p-0 border-0"
                                    title="Delete Row"
                                    onClick={() => deleteItemRow(sIdx, siIdx, si.id)}
                                  >
                                    <i className="fa fa-trash"></i>
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      <div className="mt-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => addItemRow(sIdx)}
                          className="border-dashed"
                        >
                          <i className="fa fa-plus me-1"></i> Add Row
                        </Button>
                      </div>
                    </>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
      )}

      {/* Add Shape Modal */}
      {showAddShapePrompt && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 10000 }}>
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header py-2 bg-light border-0">
                <h6 className="modal-title small fw-bold">Add New Shape</h6>
                <button type="button" className="btn-close" onClick={() => setShowAddShapePrompt(false)}></button>
              </div>
              <div className="modal-body py-4">
                <Form.Control
                  size="sm"
                  placeholder="Enter shape name (e.g. HEXAGON)"
                  value={newShapeName}
                  autoFocus
                  className="shadow-sm"
                  onChange={(e) => setNewShapeName(e.target.value)}
                />
              </div>
              <div className="modal-footer py-2 border-0 bg-light">
                <Button size="sm" variant="link" className="text-muted text-decoration-none" onClick={() => setShowAddShapePrompt(false)}>Cancel</Button>
                <Button size="sm" variant="primary" className="px-3" onClick={handleAddNewShape}>Add Shape</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationMountingStructures;
